import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  useColorModeValue,
  Badge,
  Flex,
  Input,
  Select,
  Text,
  Avatar,
  Stack,
  IconButton,
  Icon,
  Button,
  SimpleGrid,
} from "@chakra-ui/react";
import moment from "moment";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { getAdminPurchases } from "api/billingPlan";
import type { AdminPurchase } from "types/billing/UserPurchasesResponse";
import { TypingIndicator } from "screens/thread/components/cells/components";
import { useButtonProps, useInfiniteLoading } from "hooks";
import { useUsersByIds } from "hooks/useUsers";
import { VscClearAll } from "react-icons/vsc";
import { FiDownload } from "react-icons/fi";

const PAGE_SIZE = 50;

interface PurchasesFilters {
  search: string;
  objectType: string;
  paymentStatus: string;
  transactionId: string;
  creationDateFrom: string;
  creationDateTo: string;
}

const PurchasesAdmin = () => {
  const [allPurchases, setAllPurchases] = useState<AdminPurchase[]>([]);
  const [filteredPurchases, setFilteredPurchases] = useState<AdminPurchase[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState<PurchasesFilters>({
    search: "",
    objectType: "",
    paymentStatus: "",
    transactionId: "",
    creationDateFrom: "",
    creationDateTo: "",
  });

  // Get unique user IDs from purchases
  const userIds = useMemo(() => {
    return [...new Set(allPurchases.map((purchase) => purchase.userId))];
  }, [allPurchases]);

  // Use the new hook to fetch users
  const { users } = useUsersByIds(userIds);

  const bgColor = useColorModeValue("gray.200", "gray.700");
  const titleColor = useColorModeValue("charli.lightGray", "gray.500");
  const hoverBgColor = useColorModeValue("gray.50", "gray.600");
  const buttonColor = useColorModeValue("primary.default", "primary.default");
  const chartBgColor = useColorModeValue("white", "gray.800");
  const tagColorValue = useColorModeValue("600", "600");
  const primaryButtonProps = useButtonProps("sm", "primary");
  const COLORS = ["#8cb4d8", "#fa9797", "#dacd85", "#bba8da", "#92d488"];

  // Chart data processing
  const topUsersData = useMemo(() => {
    const userCounts = allPurchases.reduce((acc, purchase) => {
      acc[purchase.userId] = (acc[purchase.userId] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(userCounts)
      .map(([userId, count]) => ({
        name: users[userId]?.fullName || `Anonymous ${userId.slice(0, 5)}`,
        value: count,
        avatarUrl: users[userId]?.avatarUrl,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [allPurchases, users]);

  const paymentStatusData = useMemo(() => {
    const paidCount = allPurchases.filter((p) => p.paymentDate !== null).length;
    const unpaidCount = allPurchases.length - paidCount;
    return [
      { name: "Paid", value: paidCount },
      { name: "Unpaid", value: unpaidCount },
    ];
  }, [allPurchases]);

  const reportTypeData = useMemo(() => {
    const typeCounts = allPurchases.reduce((acc, purchase) => {
      acc[purchase.objectType] = (acc[purchase.objectType] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(typeCounts).map(([typeName, count]) => ({
      name: typeName,
      value: count,
    }));
  }, [allPurchases]);

  const purchasesByDayData = useMemo(() => {
    const last14Days = Array.from({ length: 14 }, (_, i) => moment().subtract(i, "days").format("YYYY-MM-DD")).reverse();
    const purchasesMap = last14Days.reduce((acc, date) => {
      acc[date] = { investment: 0, scorecard: 0 };
      return acc;
    }, {} as Record<string, { investment: number; scorecard: number }>);

    allPurchases.forEach((purchase) => {
      const purchaseDate = moment(purchase.creationDate).format("YYYY-MM-DD");
      if (Object.prototype.hasOwnProperty.call(purchasesMap, purchaseDate)) {
        if (purchase.objectType === "investment") {
          purchasesMap[purchaseDate].investment++;
        } else if (purchase.objectType === "scorecard") {
          purchasesMap[purchaseDate].scorecard++;
        }
      }
    });

    return Object.entries(purchasesMap).map(([date, counts]) => ({
      date: moment(date).format("MMM DD"),
      investment: counts.investment,
      scorecard: counts.scorecard,
    }));
  }, [allPurchases]);

  // Fetch all purchases data once
  useEffect(() => {
    const fetchPurchases = async () => {
      try {
        setIsLoading(true);
        const data = await getAdminPurchases();
        setAllPurchases(data);
      } catch (err) {
        console.error("Error fetching purchases:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPurchases();
  }, []);

  // Get unique object types for filter dropdown
  const objectTypes = useMemo(() => {
    const types = new Set(allPurchases.map((p) => p.objectType));
    return Array.from(types).sort();
  }, [allPurchases]);

  // Filter purchases based on current filters
  useEffect(() => {
    let filtered = allPurchases;

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.userName.toLowerCase().includes(searchLower) ||
          p.userEmail?.toLowerCase().includes(searchLower) ||
          p.objectName.toLowerCase().includes(searchLower) ||
          p.objectType.toLowerCase().includes(searchLower)
      );
    }

    if (filters.transactionId) {
      const transactionIdLower = filters.transactionId.toLowerCase();
      filtered = filtered.filter((p) => p.paymentProcessorTransactionId?.toLowerCase().includes(transactionIdLower));
    }

    if (filters.creationDateFrom) {
      const fromDate = moment(filters.creationDateFrom).startOf("day");
      filtered = filtered.filter((p) => moment(p.creationDate).isSameOrAfter(fromDate));
    }

    if (filters.creationDateTo) {
      const toDate = moment(filters.creationDateTo).endOf("day");
      filtered = filtered.filter((p) => moment(p.creationDate).isSameOrBefore(toDate));
    }

    if (filters.objectType) {
      filtered = filtered.filter((p) => p.objectType === filters.objectType);
    }

    if (filters.paymentStatus) {
      if (filters.paymentStatus === "paid") {
        filtered = filtered.filter((p) => p.paymentDate !== null);
      } else if (filters.paymentStatus === "unpaid") {
        filtered = filtered.filter((p) => p.paymentDate === null);
      }
    }

    setFilteredPurchases(filtered);
  }, [allPurchases, filters]);

  // Client-side pagination function for useInfiniteLoading
  const loadPurchasesPage = useCallback(
    async (nextToken: string | null) => {
      const startIndex = nextToken ? parseInt(nextToken, 10) : 0;
      const endIndex = startIndex + PAGE_SIZE;
      const pageData = filteredPurchases.slice(startIndex, endIndex);
      const hasMore = endIndex < filteredPurchases.length;

      return {
        data: pageData,
        nextToken: hasMore ? endIndex.toString() : null,
        totalCount: filteredPurchases.length,
      };
    },
    [filteredPurchases]
  );

  const {
    loading: isInfiniteLoading,
    items: displayedPurchases,
    lastMessageObserverRef,
    hasNextPage,
    clear,
  } = useInfiniteLoading<AdminPurchase>({
    loadItems: loadPurchasesPage,
  });

  // Clear and reload when filters change
  useEffect(() => {
    clear();
  }, [filters, clear]);

  // Trigger initial load when data becomes available
  useEffect(() => {
    if (!isLoading && filteredPurchases.length > 0 && displayedPurchases.length === 0) {
      clear();
    }
  }, [isLoading, filteredPurchases.length, displayedPurchases.length, clear]);

  const handleFilterChange = useCallback((key: keyof PurchasesFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters({
      search: "",
      objectType: "",
      paymentStatus: "",
      transactionId: "",
      creationDateFrom: "",
      creationDateTo: "",
    });
  }, []);

  const getPaymentStatusBadge = (paymentDate: string | null) => {
    if (paymentDate) {
      return <Badge colorScheme="green">Paid</Badge>;
    }
    return <Badge colorScheme="red">Unpaid</Badge>;
  };

  if (isLoading) {
    return (
      <Flex height="200px" align="center" justify="center">
        <TypingIndicator />
      </Flex>
    );
  }
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box bg={chartBgColor} p={2} borderRadius="md" shadow="md">
          <Flex align="center">
            <Avatar size="xs" name={data.name} src={data.avatarUrl} mr={2} />
            <Text fontSize="sm">{`${data.name} - ${data.value} reports`}</Text>
          </Flex>
        </Box>
      );
    }
    return null;
  };

  const CustomLegend = (props: any) => {
    const { payload } = props;

    return (
      <Stack direction="row" spacing={2} align="center" mt={1} justifyContent="center" flexWrap="wrap">
        {payload.map((entry: any, index: number) => (
          <Flex key={`item-${index}`} align="center" width="auto" justifyContent="center">
            <Box w="12px" h="12px" bg={entry.color} mr={2} borderRadius="sm" />
            <Avatar size="xs" name={entry.payload.name} src={entry.payload.avatarUrl} mr={2} />
            <Text fontSize="sm">{entry.payload.name}</Text>
          </Flex>
        ))}
      </Stack>
    );
  };

  return (
    <Box>
      <Box mb={"3rem"} p={1} bg={chartBgColor}>
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={3}>
          <Box>
            <Text fontSize="lg" fontWeight="bold" mb={2} textAlign="center">
              Top 5 Users by Purchases
            </Text>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={topUsersData} dataKey="value" nameKey="name" cx="50%" cy="40%" outerRadius={80} fill="#8884d8">
                  {topUsersData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend content={<CustomLegend />} verticalAlign="bottom" />
              </PieChart>
            </ResponsiveContainer>
          </Box>
          <Box>
            <Text fontSize="lg" fontWeight="bold" textAlign="center">
              Payment Status
            </Text>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={paymentStatusData} dataKey="value" nameKey="name" cx="50%" cy="40%" outerRadius={80} fill="#82ca9d">
                  <Cell fill={COLORS[0]} />
                  <Cell fill={COLORS[1]} />
                </Pie>
                <Tooltip labelStyle={{ fontSize: "14px", fontWeight: "bold" }} itemStyle={{ fontSize: "14px", fontWeight: "bold" }} />
                <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: "14px" }} />
              </PieChart>
            </ResponsiveContainer>
          </Box>
          <Box>
            <Text fontSize="lg" fontWeight="bold" textAlign="center">
              Report Types
            </Text>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={reportTypeData} dataKey="value" nameKey="name" cx="50%" cy="40%" outerRadius={80} fill="#8884d8">
                  {reportTypeData.map((entry) => (
                    <Cell key={`cell-${entry.name}`} fill={entry.name === "investment" ? COLORS[4] : COLORS[0]} />
                  ))}
                </Pie>
                <Tooltip labelStyle={{ fontSize: "14px", fontWeight: "bold" }} itemStyle={{ fontSize: "14px", fontWeight: "bold" }} />
                <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: "14px" }} />
              </PieChart>
            </ResponsiveContainer>
          </Box>
          <Box>
            <Text fontSize="lg" fontWeight="bold" mb={2} textAlign="center">
              Daily Purchases (Last 14 Days)
            </Text>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={purchasesByDayData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip labelStyle={{ fontSize: "14px", fontWeight: "bold" }} itemStyle={{ fontSize: "14px", fontWeight: "bold" }} />
                <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: "14px" }} />
                <Bar dataKey="investment" stackId="a" fill={COLORS[4]} />
                <Bar dataKey="scorecard" stackId="a" fill={COLORS[0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </SimpleGrid>

        <Stack direction="row" spacing={2} mt={2} justifyContent="flex-end">
          <Button {...primaryButtonProps} onClick={() => window.open("https://mixpanel.com/s/3b5NbJ", "_blank")}>
            Open Mixpanel
          </Button>
        </Stack>
      </Box>

      <Box mb={1} p={1}>
        <Stack direction="row" spacing={4} alignItems="center" justifyContent="space-between" width="100%">
          <Box width="100%">
            <Text fontSize="sm" mb={1}>
              User, email, report name...
            </Text>
            <Input value={filters.search} onChange={(e) => handleFilterChange("search", e.target.value)} size="sm" />
          </Box>
          <Box width="100%">
            <Text fontSize="sm" mb={1}>
              Transaction ID
            </Text>
            <Input
              placeholder="Search by transaction ID..."
              value={filters.transactionId}
              onChange={(e) => handleFilterChange("transactionId", e.target.value)}
              size="sm"
            />
          </Box>
          <Box width="100%">
            <Text fontSize="sm" mb={1}>
              Creation Date From
            </Text>
            <Input
              type="date"
              value={filters.creationDateFrom}
              onChange={(e) => handleFilterChange("creationDateFrom", e.target.value)}
              size="sm"
            />
          </Box>
          <Box width="100%">
            <Text fontSize="sm" mb={1}>
              Creation Date To
            </Text>
            <Input
              type="date"
              value={filters.creationDateTo}
              onChange={(e) => handleFilterChange("creationDateTo", e.target.value)}
              size="sm"
            />
          </Box>
          <Box width="100%">
            <Text fontSize="sm" mb={1}>
              Report Type
            </Text>
            <Select
              placeholder="All types"
              value={filters.objectType}
              onChange={(e) => handleFilterChange("objectType", e.target.value)}
              size="sm">
              {objectTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Select>
          </Box>
          <Box width="100%">
            <Text fontSize="sm" mb={1}>
              Payment Status
            </Text>
            <Select
              placeholder="All statuses"
              value={filters.paymentStatus}
              onChange={(e) => handleFilterChange("paymentStatus", e.target.value)}
              size="sm">
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
            </Select>
          </Box>
          <Box width="3rem">
            <Text fontSize="sm" mb={1}>
              Reset
            </Text>
            <IconButton
              aria-label="Clear Filters"
              size="sm"
              variant="outline"
              onClick={handleResetFilters}
              icon={<Icon as={VscClearAll} fontSize=".9rem" />}
            />
          </Box>
        </Stack>
        <Stack direction="row" spacing={2} mt={2} alignItems="center" justifyContent="space-between">
          <Text fontSize="xs" mt={2} color={titleColor}>
            Showing {displayedPurchases.length} of {filteredPurchases.length} purchases
          </Text>
        </Stack>
      </Box>

      {/* Table header */}
      <Box fontSize="small" borderBottomColor={bgColor} borderBottom="2px solid" mb={1} color={titleColor} display="flex" p={2}>
        <Box width="200px">User</Box>
        <Box width="200px">Email</Box>
        <Box width="150px">Report Type</Box>
        <Box width="200px">Report Name</Box>
        <Box width="150px">Creation Date</Box>
        <Box width="150px">Payment Date</Box>
        <Box width="100px">Status</Box>
        <Box width="150px">Transaction ID</Box>
        <Box flex={1}>Download Report</Box>
      </Box>

      {/* Table rows */}
      {displayedPurchases.map((purchase) => (
        <Flex
          key={purchase.id}
          p={2}
          alignItems="center"
          _hover={{ bgColor: hoverBgColor }}
          borderBottom="1px solid"
          borderBottomColor={bgColor}>
          <Box width="200px" display="flex" alignItems="center">
            <Avatar size="xs" name={purchase.userName} src={users[purchase.userId]?.avatarUrl || undefined} mr={2} />
            <Text fontSize="sm" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
              {purchase.userName.startsWith("User ") ? "Anonymous" : purchase.userName}
            </Text>
          </Box>
          <Box width="200px">
            <Text fontSize="sm" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
              {purchase.userEmail || "—"}
            </Text>
          </Box>
          <Box width="150px">
            <Box
              fontSize={"11px"}
              borderRadius={"md"}
              fontWeight={"bold"}
              color={purchase.objectType === "investment" ? `green.${tagColorValue}` : `blue.${tagColorValue}`}>
              {purchase.objectType.toUpperCase()}
            </Box>
          </Box>
          <Box width="200px">
            <Text fontSize="sm" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
              {purchase.objectName}
            </Text>
          </Box>
          <Box width="150px">
            <Text fontSize="sm">{moment(purchase.creationDate).format("MMM DD, YYYY")}</Text>
          </Box>
          <Box width="150px">
            <Text fontSize="sm">{purchase.paymentDate ? moment(purchase.paymentDate).format("MMM DD, YYYY") : "—"}</Text>
          </Box>
          <Box width="100px">{getPaymentStatusBadge(purchase.paymentDate)}</Box>
          <Box width="150px">
            <Text fontSize="sm" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
              {purchase.paymentProcessorTransactionId || "—"}
            </Text>
          </Box>
          <Box flex={1}>
            {purchase.downloadUrl ? (
              <Icon
                onClick={(e) => {
                  e.preventDefault();
                  purchase.downloadUrl && window.open(purchase.downloadUrl, "_blank");
                }}
                _hover={{}}
                _active={{}}
                aria-label="Download report"
                as={FiDownload}
                opacity={purchase.downloadUrl ? 1 : 0}
                color={buttonColor}
                fontSize={"1.4rem"}
              />
            ) : (
              <Text fontSize="sm" color={titleColor}>
                —
              </Text>
            )}
          </Box>
        </Flex>
      ))}

      {/* Infinite loading indicator */}
      {hasNextPage && (
        <Flex
          visibility={isInfiniteLoading ? "visible" : "hidden"}
          height="3rem"
          ref={lastMessageObserverRef}
          align="center"
          justify="center">
          <TypingIndicator />
        </Flex>
      )}

      {filteredPurchases.length === 0 && !isLoading && !isInfiniteLoading && (
        <Box textAlign="center" py={8}>
          <Text color={titleColor}>No purchases found matching the current filters.</Text>
        </Box>
      )}
    </Box>
  );
};

export { PurchasesAdmin };
export default PurchasesAdmin;
